import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback} from 'react';

import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ProcedureMethodNodeRendererSection} from 'test/lib/cypress/shared/ProcedureMethodNodeRendererSections';
import {GRAY, GRAY_DARKER} from 'ui-library/Colors';

import {
  getMethodComponentCid,
  getMethodComponentName,
  ProcedureDataParameterSetting,
} from '../ProcedureDomainTypes';
import {createHandleIdForMaterial} from './handleIdentifierUtils';
import {ProcedureMaterialInputHandle, ProcedureMaterialOutputHandle} from './ProcedureHandles';
import {textClass, SUB_ELEMENT_HEIGHT, SUB_TITLE_HEIGHT} from './procedureMethodNodeCommonConsts';
import {MethodCanvasNodeData} from './ProcedureReactFlowCanvasTypes';

function getComponentDisplayName(methodComponent: {name: string}): JSX.Element | string {
  return methodComponent.name || <span className={css(styles.emptyNameText)}>{'<no name>'}</span>;
}

function DataParameterDisplay(props: {parameter: ProcedureDataParameterSetting}): JSX.Element {
  const p = props.parameter;
  const value = p.values.length > 0 ? (p.values[0].value.value ?? '') : '';
  // Only show units if there is a setpoint value
  const unit = value.length > 0 ? (p.dataParameterVersion.dataType.unit?.symbol ?? '') : '';
  const valueString: string | null = value ? `\u00A0${value}\u00A0${unit}` : null;

  return (
    <div className="u-lr-split">
      <div className={css(styles.subElementName)}>{getMethodComponentName(p)}</div>
      <div
        className={css(styles.setPointValue)}
        {...getTestProps({component: 'DataParameterDisplay', element: 'setpointValueWithUnit'})}
      >
        {valueString}
      </div>
    </div>
  );
}

function isMaterialHandleConnected(handleId: string, handlesInUse: Array<string>): boolean {
  return handlesInUse.includes(handleId);
}

export function ProcedureMethodSectionElementWrapper({
  children,
  dataTestAttrs,
}: {
  children: React.ReactNode;
  dataTestAttrs: DataTestAttrs;
}): JSX.Element {
  const subElementClass = classNames(textClass, css(styles.subElement));
  return (
    <div className={subElementClass} {...getTestProps(dataTestAttrs)}>
      {children}
    </div>
  );
}

export function ProcedureMethodSectionWrapper(
  props: React.PropsWithChildren<{
    sectionTitle: string;
    isEmpty: boolean;
  }>
): JSX.Element {
  const subTitleClass = classNames(textClass, css(styles.subTitle));

  return (
    <div
      className={css(
        styles.methodNodeSection,
        props.isEmpty ? styles.emptyMethodNodeSection : styles.nonEmptyMethodNodeSection
      )}
    >
      <div className={subTitleClass}>{props.sectionTitle}</div>
      {props.children}
    </div>
  );
}
export interface MethodSectionProps {
  sectionTitle: string;
  data: MethodCanvasNodeData;
}

export function ProcedureParametersMethodSection(props: MethodSectionProps): JSX.Element {
  const {dataParameterSettings} = props.data;
  return (
    <ProcedureMethodSectionWrapper
      sectionTitle={props.sectionTitle}
      isEmpty={dataParameterSettings.length === 0}
    >
      {dataParameterSettings.map((p) => (
        <ProcedureMethodSectionElementWrapper
          key={getMethodComponentCid(p)}
          dataTestAttrs={{
            component: 'ProcedureMethodSectionElementWrapper',
            element: ProcedureMethodNodeRendererSection.DATA_PARAMETERS,
            key: getMethodComponentName(p),
          }}
        >
          <DataParameterDisplay parameter={p} />
        </ProcedureMethodSectionElementWrapper>
      ))}
    </ProcedureMethodSectionWrapper>
  );
}

export function ProcedureEquipmentMethodSection(props: MethodSectionProps): JSX.Element {
  const {equipmentSettings} = props.data;
  return (
    <ProcedureMethodSectionWrapper sectionTitle={props.sectionTitle} isEmpty={equipmentSettings.length === 0}>
      {equipmentSettings.map((equip) => (
        <ProcedureMethodSectionElementWrapper
          key={getMethodComponentCid(equip)}
          dataTestAttrs={{
            component: 'ProcedureMethodSectionElementWrapper',
            element: ProcedureMethodNodeRendererSection.EQUIPMENT,
            key: getMethodComponentName(equip),
          }}
        >
          <div className={css(styles.subElementName)}>
            {getComponentDisplayName({name: getMethodComponentName(equip)})}
          </div>
        </ProcedureMethodSectionElementWrapper>
      ))}
    </ProcedureMethodSectionWrapper>
  );
}

export interface MaterialMethodSectionProps extends MethodSectionProps {
  methodCid: string;
  isLeafNode: boolean;
  handlesInUse: Array<string>;
  isHovered: boolean;
  isProcedureReadOnly: boolean;
  accessLevel: ProcedureAccessLevel | null;
}

export function ProcedureMaterialsMethodSection(props: MaterialMethodSectionProps): JSX.Element {
  const {data, handlesInUse, isHovered, isLeafNode, methodCid, isProcedureReadOnly, accessLevel} = props;
  const isMaterialHandleVisible = useCallback(
    (handleId: string) =>
      isMaterialHandleConnected(handleId, handlesInUse) ||
      (isHovered && !isProcedureReadOnly && accessLevel === ProcedureAccessLevel.FULL_ACCESS),
    [handlesInUse, isHovered, isProcedureReadOnly, accessLevel]
  );

  return (
    <>
      {/* Material Inputs */}
      <ProcedureMethodSectionWrapper
        sectionTitle={`${props.sectionTitle} inputs`}
        isEmpty={data.materialInputSettings.length === 0}
      >
        {data.materialInputSettings.map((materialInputSetting) => {
          const materialCid = getMethodComponentCid(materialInputSetting);
          const handleId = createHandleIdForMaterial(methodCid, materialCid, 'target');
          const materialInputName = getMethodComponentName(materialInputSetting);

          return (
            <ProcedureMethodSectionElementWrapper
              key={materialCid}
              dataTestAttrs={{
                component: 'ProcedureMethodSectionElementWrapper',
                element: ProcedureMethodNodeRendererSection.MATERIAL_INPUTS,
                key: materialInputName,
              }}
            >
              <div className={css(styles.subElementName)}>
                {getComponentDisplayName({name: materialInputName})}
              </div>
              <ProcedureMaterialInputHandle
                id={handleId}
                // Hide handle by default to prevent starting an edge from material inputs. The handle will
                // still appear when a connection is being made from a material output.
                isVisible={false}
                materialInputSetting={materialInputSetting}
              />
            </ProcedureMethodSectionElementWrapper>
          );
        })}
      </ProcedureMethodSectionWrapper>
      {/* Material Outputs */}
      <ProcedureMethodSectionWrapper
        sectionTitle={`${props.sectionTitle} outputs`}
        isEmpty={data.materialOutputSettings.length === 0}
      >
        {data.materialOutputSettings.map((materialOutputSetting) => {
          const materialCid = getMethodComponentCid(materialOutputSetting);
          const handleId = createHandleIdForMaterial(methodCid, materialCid, 'source');
          const materialName = getMethodComponentName(materialOutputSetting);
          return (
            <ProcedureMethodSectionElementWrapper
              key={materialCid}
              dataTestAttrs={{
                component: 'ProcedureMethodSectionElementWrapper',
                element: ProcedureMethodNodeRendererSection.MATERIAL_OUTPUTS,
                key: materialName,
              }}
            >
              <div className={css(styles.subElementName)}>
                {getComponentDisplayName({name: getMethodComponentName(materialOutputSetting)})}
              </div>
              <ProcedureMaterialOutputHandle
                id={handleId}
                isVisible={!isLeafNode && isMaterialHandleVisible(handleId)}
                materialOutputSetting={materialOutputSetting}
              />
            </ProcedureMethodSectionElementWrapper>
          );
        })}
      </ProcedureMethodSectionWrapper>
    </>
  );
}

const styles = StyleSheet.create({
  emptyNameText: {
    color: GRAY,
  },
  methodNodeSection: {
    borderTop: `1px solid ${GRAY}`,
    paddingTop: '4px',
    paddingBottom: '6px',
  },
  emptyMethodNodeSection: {
    paddingBottom: '4px',
  },
  nonEmptyMethodNodeSection: {
    paddingBottom: '6px',
  },
  subTitle: {
    fontWeight: 'bold',
    fontSize: 13,
    padding: '4px 8px',
    height: `${SUB_TITLE_HEIGHT}px`,
  },
  subElement: {
    fontSize: 12,
    padding: '2px 8px',
    position: 'relative',
    overflow: 'visible',
    height: `${SUB_ELEMENT_HEIGHT}px`,
  },
  subElementName: {
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  setPointValue: {
    color: GRAY_DARKER,
  },
});
